import { env } from '@/env'

/**
 * Default per-request timeout. If the backend hasn't responded within
 * this window we assume it's down and fail fast with `TimeoutError`
 * instead of leaving the user staring at a spinner for the browser's
 * (much longer) native fetch timeout.
 */
export const DEFAULT_TIMEOUT_MS = 4_000

/**
 * Thrown when the backend fails to respond within the timeout window.
 * Distinct from `ApiError` so the UI/Sentry can tag "backend down or
 * unreachable" separately from "backend answered with an error".
 */
export class TimeoutError extends Error {
  constructor(ms: number) {
    super(`Request timed out after ${ms}ms`)
    this.name = 'TimeoutError'
  }
}

/**
 * Thrown when the backend responds 401. Surfaced separately from other
 * errors so the accounts hook can trigger a silent renew / re-auth instead
 * of rendering a generic error card.
 */
export class UnauthorizedError extends Error {
  readonly status = 401 as const
  constructor(message = 'Unauthorized') {
    super(message)
    this.name = 'UnauthorizedError'
  }
}

/**
 * Thrown for any non-2xx response that isn't a 401. `status` lets the UI
 * and Sentry distinguish 4xx (user fixable, retry unlikely to help) from
 * 5xx (transient, retry affordance appropriate).
 */
export class ApiError extends Error {
  readonly status: number
  readonly body: string
  constructor(status: number, body: string) {
    super(`HTTP ${status}: ${body || 'no body'}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/**
 * Minimal typed fetch wrapper.
 *
 * - Hits `${VITE_API_BASE}${path}` with `Accept: application/json`.
 * - Attaches `Authorization: Bearer ${token}` when a token is provided.
 * - Aborts after `timeoutMs` (default `DEFAULT_TIMEOUT_MS`) and throws
 *   `TimeoutError` — an unresponsive backend should surface as an error
 *   card quickly, not as a multi-second hang.
 * - Throws `UnauthorizedError` on 401, `ApiError` on any other non-2xx.
 *   Errors propagate so TanStack Query owns the retry / error state —
 *   we never swallow them here per the Phase 4a guardrail.
 */
export async function apiGet<T>(
  path: string,
  token: string | null,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  let res: Response
  try {
    res = await fetch(`${env.VITE_API_BASE}${path}`, {
      method: 'GET',
      headers,
      credentials: 'omit',
      signal: AbortSignal.timeout(timeoutMs),
    })
  } catch (err) {
    if (
      err instanceof DOMException &&
      (err.name === 'TimeoutError' || err.name === 'AbortError')
    ) {
      throw new TimeoutError(timeoutMs)
    }
    throw err
  }

  if (res.status === 401) {
    throw new UnauthorizedError()
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new ApiError(res.status, body)
  }

  return (await res.json()) as T
}
